import {
  Component, ViewEncapsulation, ChangeDetectionStrategy,
  Input, Output, EventEmitter, ChangeDetectorRef, OnInit
} from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';

import isEmpty from 'lodash-es/isEmpty';
import { FormInputType, FormDocumentInputTemplate } from '@forms/form-text-input/entities/input-type';
import { FormInputsService } from '@reservation/services/form-inputs.service';
import { InputMask, InputRegex } from '@forms/form-text-input/entities/input-mask';
import { BookingDocumentError } from '@app/entities/booking-documents';
import { BookingFormGeneralErrorTypes } from '@app/entities/booking-form-general-error';
import { isNullOrUndefined } from '@app/util/util';
import { replaceDiacritics } from '@shared/directives/mappers/replace-diacritics';

@Component({
  selector: 'app-form-text-input',
  templateUrl: './form-text-input.component.html',
  styleUrls: ['./form-text-input.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FormTextInputComponent implements OnInit {

  @Input() placeholder: string;
  @Input() label: string;
  @Input() required: boolean;
  @Input() id: string;
  @Input() maxLength: number;
  @Input() style: string;
  @Input() formControlSelector: string;
  @Input() form: UntypedFormGroup;
  @Input() isSubmitted: boolean = false;
  @Input() formInputType: FormInputType = FormInputType.FULL_TEXT;
  @Input() inputDocumentType: FormDocumentInputTemplate = FormDocumentInputTemplate.ALPHANUMERIC;
  @Input() hiddenPlaceholder: boolean;
  @Input() documentCustomErrors: BookingDocumentError[];
  @Input() readOnly: boolean;
  @Input() hasInternationalDocumentError: boolean;
  @Input() customMask = '';
  @Input() showInnerErrors = true;

  @Output() keyUp: EventEmitter<Event> = new EventEmitter();
  @Output() clickEmmiter: EventEmitter<Event> = new EventEmitter();

  public active: boolean;

  public readonly onlyLettersPatterns = { 'S': { pattern: /^[a-zA-ZÀ-ÖØ-öø-ÿ\s]+/ } };

  private inputModel = null;

  constructor(
    private readonly formInputsService: FormInputsService,
    private readonly changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (isNullOrUndefined(this.label)) {
      this.label = this.placeholder;
    }
  }

  get inputMask(): typeof InputMask {
    return InputMask;
  }

  get inputRegex(): typeof InputRegex {
    return InputRegex;
  }

  get isEmptyPlaceholder(): boolean {
    return !this.placeholder || this.hiddenPlaceholder;
  }

  get isEmptyValue(): boolean {
    return !!this.form?.get(this.formControlSelector) && !this.form?.get(this.formControlSelector)?.value;
  }

  get showGeneralError(): boolean {
    return !this.hasInternationalDocumentError && (!!this.isEmptyValue);
  }

  get hasCustomDocumentErrorMessage(): boolean {
    return !!this.hasCustomDocumentError &&
      (this.hasCustomMaskError || !!this.hasCustomInvalidDocumentValueError);
  }

  get hasCustomMaskError(): boolean {
    return !!this.customMaskError && !!this.getFieldErrors.includes(BookingFormGeneralErrorTypes.MASK);
  }

  get hasCustomInvalidDocumentValueError(): boolean {
    return !!this.customMaskError && !!this.getFieldErrors.includes(BookingFormGeneralErrorTypes.INVALID_DOCUMENT_VALUE);
  }

  get errorWithPattern(): boolean {
    return this.isOnlyLettersInput && this.getFieldErrors.includes(BookingFormGeneralErrorTypes.PATTERN);
  }

  get errorWithEmailPattern(): boolean {
    return this.isEmailInput && this.getFieldErrors.includes(BookingFormGeneralErrorTypes.PATTERN);
  }

  get hasCustomDocumentError(): boolean {
    return this.isDocumentInput && !!this.documentCustomErrors?.length;
  }

  get customMaskError(): BookingDocumentError {
    return !!this.hasCustomDocumentError && this.documentCustomErrors.find(error =>
      error.TYPE === BookingFormGeneralErrorTypes.MASK);
  }

  get customInvalidDocumentValueError(): BookingDocumentError {
    return !!this.hasCustomDocumentError && this.documentCustomErrors.find(error =>
      error.TYPE === BookingFormGeneralErrorTypes.INVALID_DOCUMENT_VALUE);
  }

  get isActivate(): boolean {
    return !!this.form && !!this.formControlSelector && !!this.form?.get(this.formControlSelector) &&
      !isEmpty(this.form?.get(this.formControlSelector)?.value);
  }

  get formInputTypes(): typeof FormInputType {
    return FormInputType;
  }

  get formDocumentInputTemplates(): typeof FormDocumentInputTemplate {
    return FormDocumentInputTemplate;
  }

  get isDocumentInput(): boolean {
    return this.formInputType === this.formInputTypes.DOCUMENT;
  }

  get placeholderText(): string {
    return this.active ? this.placeholder : this.label;
  }

  get customAttrType(): string {
    return this.customMask === InputMask.DOB ? 'tel' : 'text'
  }

  private get isOnlyLettersInput(): boolean {
    return this.formInputType === this.formInputTypes.ONLY_LETTERS;
  }

  private get isEmailInput(): boolean {
    return this.formInputType === this.formInputTypes.EMAIL;
  }

  private get getFieldErrors(): string[] {
    const errors: ValidationErrors = this.form?.get(this.formControlSelector)?.errors;
    if (errors) return Object.keys(errors);
    else return [];
  }

  public resetInput(): void {
    this.form?.patchValue({ [this.formControlSelector]: '' });
    this.changeDetector.detectChanges();
  }

  public addErrorClass(field: string): { [key: string]: boolean } {
    return this.formInputsService.addErrorClass(field, this.form, this.isSubmitted);
  }

  public changeValue(value: string): void {
    if (!value) this.changeDetector.detectChanges();
  }

  public selectionInput(e: KeyboardEvent): void {
    this.keyUp.emit(e);
    this.changeDetector.detectChanges();
  }

  public clickEvent(event: Event): void {
    this.clickEmmiter.emit(event);
    this.changeDetector.detectChanges();
  }

  public setActive(bool: boolean): void {
    this.active = bool;
  }

  public outputTransformFn(): void {
    const formControl = this.form?.get(this.formControlSelector);
    if (formControl && this.inputModel !== formControl?.value) {
      const value = formControl.value;
      const transform = value ? this.capitalize(replaceDiacritics(value as string)) : '';
      this.inputModel = transform;
      formControl.setValue(transform);
    }
  }

  private capitalize(value: string): string {
    return value.toLowerCase().split(' ').map(v => v !== '' ? v[0].toUpperCase() + v.substr(1) : '').join(' ');
  }

}
